// The run-a-report job, ported from the coffee DataRunView (updateData / checkData /
// delayUpdateData). Two transports reach the same envelope shape: v2 fetches the question in two
// hops (report index for the hashes, report items for the data) and the legacy path posts one
// aggregate query, so checkData below reads either unchanged.

import { addCSRF } from '@aytm/app/stats/lib/util/helpers'
import { adaptItemsResponse, callReportIndex, callReportItems, getModelValue, getQueryUrl, getReportConfig, isReportXpert, useStatsV2 } from '@aytm/app/stats/lib/stat-page'
import { questionOldIdToId } from '@aytm/app/stats/page/components/lib/utils'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

export const JOB_POLL_DELAY = 15000

const postJson = (url, data, { onSuccess, onError }) => {
  const controller = new AbortController()

  fetch(url, {
    method: 'POST',
    cache: 'no-store',
    // the poll goes to the stat server, which answers any origin with a wildcard; the browser
    // refuses a wildcard reply to a request carrying cookies, so cookies go to our own origin only
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    signal: controller.signal
  })
    .then(response => response.json())
    .then(onSuccess)
    .catch(error => {
      if ('AbortError' !== error?.name) onError(error)
    })

  return controller
}

// Starts the job itself: the only call that asks the server to begin crunching.
const startJob = ({ key, questionKey, schemaKey, type, jobUrl }, handlers) => {
  const data = addCSRF({
    key: key?.code ?? 0,
    ...((questionKey || schemaKey) && { include: questionKey || schemaKey }),
    ...(type && { command: type })
  })

  return postJson(jobUrl || PageGlobals.STAT_JOB_API_URL, data, handlers)
}

// v2: index for the hashes covering this question, then items for their data. The result is
// re-wrapped as a legacy aggregate envelope so the caller reads one shape.
const pollV2 = ({ key, questionKey, schemaKey, response, timestamp, queryHash, jobId }, { onSuccess, onError }) => {
  const id = questionOldIdToId(questionKey || schemaKey)

  const config = getReportConfig()
  config.conversation = 'short'
  const translation = getModelValue('translation')
  if (null != translation) config.translation = translation

  const opts = { config }
  if (!isReportXpert()) opts.scope = [id]

  const envelope = {}
  if (response?.timestamp || null != timestamp) {
    envelope.timestamp = response?.timestamp || timestamp
    envelope.jobId = response?.queryHash || queryHash || response?.jobId || jobId
  } else if (!queryHash) {
    opts.refresh = true
  }

  return callReportIndex(
    key,
    opts,
    envelope,
    (ajax, idxData) => {
      if ('OK' !== idxData?.status) return onSuccess(idxData)

      const blocks = idxData.response?.blocks || {}
      const hashes = []
      for (const block of Object.values(blocks)) {
        if ((block.questionId === id || (block.scope || []).includes(id)) && block.hash && !hashes.includes(block.hash))
          hashes.push(block.hash)
      }

      // Nothing to fetch — an OK envelope with empty stats reads as "no data" and stops polling.
      if (!hashes.length) {
        return onSuccess({
          status: 'OK',
          response: { stats: {}, timestamp: idxData.response?.timestamp, missingData: [] }
        })
      }

      return callReportItems(
        key,
        { hashes, config },
        (itemsAjax, itData) => {
          if ('OK' !== itData?.status) return onSuccess(itData)

          const adapted = adaptItemsResponse(itData.response || {})

          // missingData is always emitted (possibly empty) so the running branch can read
          // .length without a TypeError.
          return onSuccess({
            status: 'OK',
            response: {
              stats: adapted.stats,
              timestamp: itData.response?.timestamp,
              missingData: adapted.pendingHashes
            }
          })
        },
        onError
      )
    },
    onError
  )
}

// Legacy: one aggregate query, refreshed or resumed from the job it already started.
const pollLegacy = ({ key, questionKey, schemaKey, response, timestamp, queryHash, jobId }, handlers) => {
  const params = { key: key?.code ?? 0 }

  if (!isReportXpert()) params.scope = [questionOldIdToId(questionKey || schemaKey)]

  const data = { key: key?.code ?? 0, query: [['stats:aggregate', params]] }

  if (queryHash) data.jobId = queryHash
  else data.refresh = true

  const stamp = response?.timestamp || timestamp
  if (stamp) {
    data.timestamp = 'string' === typeof stamp ? new Date(stamp).getTime() : Number(stamp)
    data.jobId = response?.queryHash || queryHash || response?.jobId || jobId
  } else {
    data.refresh = true
  }

  return postJson(getQueryUrl(), data, handlers)
}

// One round trip for the question: starting the job, or polling whichever transport is in use.
export const requestJobData = ({ jobStatus, ...request }, handlers) => {
  if ('idle' === jobStatus) return startJob(request, handlers)

  return useStatsV2() ? pollV2(request, handlers) : pollLegacy(request, handlers)
}

export const calcBadData = ({ answer, type }) =>
  Boolean(
    ('hm' !== type &&
      null != answer &&
      ((Array.isArray(answer.badData) && true === answer.badData[0]) || true === answer.badData)) ||
    (answer && !Object.keys(answer).length && answer.constructor === Object)
  )

// The job's own progress reporting: the eta is inferred from how fast the percentage moved,
// capped at fifteen minutes.
export const calcJobEta = ({ jobProgress, previousProgress, previousEta }) => {
  const inferred =
    jobProgress && previousProgress ? (30 * (100 - jobProgress)) / (jobProgress - previousProgress) : 15 * 60
  const eta = Math.max(0, inferred)

  return Number.isFinite(previousEta) ? Math.min(eta, 15 * 60) : 15 * 60
}

// What the envelope means for the job: keep polling, or stop and take the answer.
export const readJobEnvelope = ({ data, answer, badData, isXpert }) => {
  if (null != data?.result) {
    if (0 < data.result.OK) return { action: 'poll', jobStatus: 'running', jobProgress: 0 }

    const jobStatus = data.result.state || 'disabled'

    return 'running' === jobStatus
      ? { action: 'poll', jobStatus, jobProgress: data.result.progress || 0, immediate: true }
      : { action: 'stop', jobStatus }
  }

  if ('OK' !== data?.status) {
    if ('INVALID KEY' === data?.status) return { action: 'refresh-key' }
    if (['RUNNING', 'WAIT'].includes(data?.status)) {
      return { action: 'poll', jobStatus: data.job || 'running', jobProgress: data.progress || 0 }
    }

    return { action: 'stop' }
  }

  // An Xpert report keeps polling while its data is still bad; otherwise an explicit status on
  // the answer wins over the bad-data heuristic.
  let jobStatus = isXpert ? (badData ? 'running' : 'disabled') : null
  if (!jobStatus && data.response?.timestamp && 0 < (data.response?.missingData?.length || 0)) jobStatus = 'running'
  if (!jobStatus) jobStatus = answer?.jobStatus
  if (!jobStatus && badData) jobStatus = 'running'
  if (!jobStatus) jobStatus = 'disabled'

  return 'running' === jobStatus
    ? { action: 'poll', jobStatus, jobProgress: answer?.jobProgress || 0 }
    : { action: 'loaded', jobStatus }
}
